import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  otherPayoutsApi,
  type JournalEntryPickDto,
} from '../../api/otherPayouts';
import { personsApi } from '../../api/persons';
import type { PersonDto } from '../../types/person';
import { useIsMirror } from '../../hooks/useIsMirror';
import { useReportScreen } from '../../components/ScreenContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import MoneyCell from '../../components/MoneyCell';

type Tab = 'other' | 'classb';

/**
 * D2 — Kitos išmokos: dvi skiltys. „Su mokesčiu" (d_kitos) maitina GPM313/
 * GPM312 kitų pajamų dėžes; „Be mokesčio" (Class B) — FR0471 ir GPM313
 * G11–G12. Įvedimas iš žurnalo įrašo: pasirink asmenį → parinkiklis rodo
 * jo įrašus su sumom. Mirror darbo sritys: tik skaityti.
 */
export default function OtherPayouts() {
  const isMirror = useIsMirror();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('other');
  const [error, setError] = useState('');

  const { data: other, isLoading: loadingOther, isError: otherError } = useQuery({
    queryKey: ['other-payouts'],
    queryFn: otherPayoutsApi.listOther,
  });
  const { data: classb, isLoading: loadingClassB } = useQuery({
    queryKey: ['classb-payouts'],
    queryFn: otherPayoutsApi.listClassB,
  });
  const { data: persons } = useQuery({
    queryKey: ['persons-all'],
    queryFn: () => personsApi.list({ limit: 200 }),
    staleTime: 5 * 60 * 1000,
  });

  const reload = () =>
    queryClient.invalidateQueries({ queryKey: [tab === 'other' ? 'other-payouts' : 'classb-payouts'] });

  const run = async (fn: () => Promise<unknown>) => {
    setError('');
    try {
      await fn();
      await reload();
    } catch (e: any) {
      setError(e.message || 'Nepavyko');
    } finally {
    }
  };

  useReportScreen(
    `Kitos išmokos (${tab === 'other' ? 'su mokesčiu' : 'be mokesčio'}): ${(tab === 'other' ? other : classb)?.length ?? 0}${isMirror ? ' (tik skaityti)' : ''}`,
    { recordCount: (tab === 'other' ? other?.length : classb?.length) ?? 0 },
  );

  const naturalPersons = (persons ?? []).filter(p => p.isNaturalPerson || p.isWorker);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Kitos išmokos</h1>
          <p className="text-sm text-gray-500">
            Išmokos natūraliems asmenims — su išskaičiuotu GPM (deklaracijų „kitos pajamos") ir be jo (B klasė, FR0471).
          </p>
        </div>
        <Link
          to="/payroll"
          className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:border-gray-400"
        >
          ← Darbo užmokestis
        </Link>
      </div>

      <div className="flex flex-wrap gap-1">
        <button
          onClick={() => setTab('other')}
          className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${tab === 'other' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'}`}
        >
          Su mokesčiu ({other?.length ?? 0})
        </button>
        <button
          onClick={() => setTab('classb')}
          className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${tab === 'classb' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'}`}
        >
          Be mokesčio — B klasė ({classb?.length ?? 0})
        </button>
      </div>

      {isMirror && <p className="text-sm text-gray-500">Ši darbo sritis tik skaityti.</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {otherError && <div className="error-message">Nepavyko įkelti išmokų — atnaujinkite puslapį.</div>}

      {tab === 'other' ? (
        <>
          {!isMirror && <OtherForm persons={naturalPersons} onDone={reload} onError={setError} />}
          {loadingOther ? <LoadingSpinner /> : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Asmuo</th>
                    <th>Kodas</th>
                    <th style={{ textAlign: 'right' }}>Net</th>
                    <th style={{ textAlign: 'right' }}>Bruto</th>
                    <th style={{ textAlign: 'right' }}>GPM %</th>
                    <th style={{ textAlign: 'right' }}>Bruto (dekl.)</th>
                    <th style={{ textAlign: 'right' }}>GPM (dekl.)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(other ?? []).map(r => (
                    <tr key={r.id}>
                      <td>{r.entryDate.slice(0, 10)}</td>
                      <td>{r.personName}</td>
                      <td>{r.incomeCode}</td>
                      <td style={{ textAlign: 'right' }}>{r.suma || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{r.sumaBruto || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{r.rate}</td>
                      <td style={{ textAlign: 'right' }}><MoneyCell value={r.grossForDeclaration} /></td>
                      <td style={{ textAlign: 'right' }}><MoneyCell value={r.taxForDeclaration} /></td>
                      <td>{!isMirror && <button onClick={() => run(() => otherPayoutsApi.deleteOther(r.id))}>Trinti</button>}</td>
                    </tr>
                  ))}
                  {(other ?? []).length === 0 && (
                    <tr><td colSpan={9} className="text-center text-gray-500">Įrašų nėra</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <>
          {!isMirror && <ClassBForm persons={naturalPersons} onDone={reload} onError={setError} />}
          {loadingClassB ? <LoadingSpinner /> : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Asmuo</th>
                    <th>Mokesčio kodas</th>
                    <th style={{ textAlign: 'right' }}>Suma</th>
                    <th>Išmokėta</th>
                    <th style={{ textAlign: 'right' }}>Mokestis</th>
                    <th style={{ textAlign: 'right' }}>Įmokėjo įmonė</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(classb ?? []).map(r => (
                    <tr key={r.id}>
                      <td>{r.personName}</td>
                      <td>{r.taxCode}</td>
                      <td style={{ textAlign: 'right' }}><MoneyCell value={r.amount} /></td>
                      <td>{r.taxPayedOutDate?.slice(0, 10) ?? '—'}</td>
                      <td style={{ textAlign: 'right' }}><MoneyCell value={r.taxDeductedAndPayed} /></td>
                      <td style={{ textAlign: 'right' }}><MoneyCell value={r.taxPayedByCompany} /></td>
                      <td>{!isMirror && <button onClick={() => run(() => otherPayoutsApi.deleteClassB(r.id))}>Trinti</button>}</td>
                    </tr>
                  ))}
                  {(classb ?? []).length === 0 && (
                    <tr><td colSpan={7} className="text-center text-gray-500">Įrašų nėra</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function useEntryPicker(personId: number | null) {
  const [entries, setEntries] = useState<JournalEntryPickDto[] | null>(null);
  const pick = async () => {
    if (!personId) return;
    try {
      setEntries(await otherPayoutsApi.pickEntries(personId));
    } catch {
      setEntries([]);
    }
  };
  return { entries, pick };
}

function OtherForm({
  persons, onDone, onError,
}: {
  persons: PersonDto[];
  onDone: () => Promise<void>;
  onError: (msg: string) => void;
}) {
  const [personId, setPersonId] = useState<number | null>(null);
  const [entryId, setEntryId] = useState<number | null>(null);
  const { entries, pick } = useEntryPicker(personId);
  const [incomeCode, setIncomeCode] = useState('12');
  const [suma, setSuma] = useState('');
  const [sumaBruto, setSumaBruto] = useState('');
  const [rate, setRate] = useState('15');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!personId || !entryId) {
      onError('Pasirinkite asmenį ir žurnalo įrašą.');
      return;
    }
    setSaving(true);
    onError('');
    try {
      await otherPayoutsApi.saveOther({
        personId, journalEntryId: entryId, incomeCode,
        suma: Number(suma) || 0, sumaBruto: Number(sumaBruto) || 0,
        rate: Number(rate) || 0,
      });
      setSuma(''); setSumaBruto('');
      await onDone();
    } catch (e: any) {
      onError(e.message || 'Nepavyko išsaugoti');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ padding: '1rem', display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">Asmuo</span>
        <select value={personId ?? ''} onChange={e => { setPersonId(Number(e.target.value) || null); setEntryId(null); pick(); }}>
          <option value="">— asmuo —</option>
          {persons.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">Žurnalo įrašas</span>
        <select value={entryId ?? ''} onChange={e => setEntryId(Number(e.target.value) || null)} disabled={!entries}>
          <option value="">— įrašas —</option>
          {(entries ?? []).map(en => (
            <option key={en.id} value={en.id}>{en.date.slice(0, 10)} {en.documentNumber} ({en.amount})</option>
          ))}
        </select>
      </label>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">Pajamų kodas</span>
        <input style={{ width: '4rem' }} value={incomeCode} onChange={e => setIncomeCode(e.target.value)} />
      </label>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">Net suma</span>
        <input style={{ width: '6rem' }} value={suma} onChange={e => setSuma(e.target.value)} placeholder="0.00" />
      </label>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">Bruto (jei žinoma)</span>
        <input style={{ width: '6rem' }} value={sumaBruto} onChange={e => setSumaBruto(e.target.value)} placeholder="0.00" />
      </label>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">GPM %</span>
        <input style={{ width: '4rem' }} value={rate} onChange={e => setRate(e.target.value)} />
      </label>
      <button onClick={submit} disabled={saving}
        className="px-4 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40">
        {saving ? '…' : 'Išsaugoti'}
      </button>
    </div>
  );
}

function ClassBForm({
  persons, onDone, onError,
}: {
  persons: PersonDto[];
  onDone: () => Promise<void>;
  onError: (msg: string) => void;
}) {
  const [personId, setPersonId] = useState<number | null>(null);
  const [entryId, setEntryId] = useState<number | null>(null);
  const { entries, pick } = useEntryPicker(personId);
  const [amount, setAmount] = useState('');
  const [taxCode, setTaxCode] = useState('32');
  const [payedOut, setPayedOut] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!personId || !entryId || !amount) {
      onError('Asmuo, įrašas ir suma privalomi.');
      return;
    }
    setSaving(true);
    onError('');
    try {
      await otherPayoutsApi.saveClassB({
        personId, journalEntryId: entryId,
        amount: Number(amount), taxCode, taxPayedOutDate: payedOut || null,
        taxDeductedAndPayed: 0, taxPayedByCompany: 0,
      });
      setAmount('');
      await onDone();
    } catch (e: any) {
      onError(e.message || 'Nepavyko išsaugoti');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ padding: '1rem', display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">Asmuo</span>
        <select value={personId ?? ''} onChange={e => { setPersonId(Number(e.target.value) || null); setEntryId(null); pick(); }}>
          <option value="">— asmuo —</option>
          {persons.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">Žurnalo įrašas</span>
        <select value={entryId ?? ''} onChange={e => setEntryId(Number(e.target.value) || null)} disabled={!entries}>
          <option value="">— įrašas —</option>
          {(entries ?? []).map(en => (
            <option key={en.id} value={en.id}>{en.date.slice(0, 10)} {en.documentNumber} ({en.amount})</option>
          ))}
        </select>
      </label>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">Suma</span>
        <input style={{ width: '6rem' }} value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
      </label>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">Mokesčio kodas</span>
        <input style={{ width: '4rem' }} value={taxCode} onChange={e => setTaxCode(e.target.value)} />
      </label>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">Išmokėta</span>
        <input type="date" value={payedOut} onChange={e => setPayedOut(e.target.value)} />
      </label>
      <button onClick={submit} disabled={saving}
        className="px-4 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40">
        {saving ? '…' : 'Išsaugoti'}
      </button>
    </div>
  );
}
